import { Card } from "@nfp-tracker/components";
import type { LucideIcon } from "lucide-react";
import { CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";

type ChecklistRowProps = {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  done?: boolean;
  /** Finished or skipped: shown muted (still with description and CTA). */
  muted?: boolean;
  /** Badges shown after the title (Done / Skipped / New). */
  badges?: ReactNode;
  /** CTA + secondary actions. */
  actions?: ReactNode;
};

/** One Getting Started checklist step, as its own card. */
export const ChecklistRow = ({
  icon: Icon,
  title,
  description,
  done = false,
  muted = false,
  badges,
  actions,
}: ChecklistRowProps) => (
  <Card
    role="group"
    aria-label={title}
    className={`flex items-start gap-4 p-4 sm:p-5 ${
      muted ? "bg-muted/30 shadow-none" : ""
    }`}
  >
    <div
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
        done ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
      }`}
    >
      {done ? (
        <CheckCircle2 className="h-5 w-5" />
      ) : (
        <Icon className="h-5 w-5" />
      )}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3
          className={`text-base leading-tight ${
            muted ? "font-medium text-foreground/80" : "font-semibold"
          }`}
        >
          {title}
        </h3>
        {badges}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      {actions && (
        <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  </Card>
);
